import {
  NEIGHBORHOOD_STATE_SCHEMA_VERSION,
  type NeighborhoodState,
  type ReputationTag,
} from "./types.ts";

export const NEIGHBORHOOD_SAVE_KEY = "vilnius.neighborhood.v1";
const REPUTATION_TAGS: readonly ReputationTag[] = [
  "helpful",
  "reliable",
  "neighbourly",
  "nosy",
  "show_off",
  "odd",
];
const QUEST_STATUSES = new Set(["not_started", "carrying", "completed"]);
const PARCEL_STATUSES = new Set(["none", "sealed", "opened", "delivered"]);
const QUEST_OUTCOMES = new Set([
  "none",
  "delivered_sealed",
  "delivered_opened",
  "entrusted_to_mantas",
]);

export function serializeNeighborhoodSave(
  state: Readonly<NeighborhoodState>,
): string {
  return JSON.stringify(state);
}

export function parseNeighborhoodSave(value: string): NeighborhoodState {
  const parsed: unknown = JSON.parse(value);
  const record = asRecord(parsed, "Neighborhood save");
  if (record.schemaVersion !== NEIGHBORHOOD_STATE_SCHEMA_VERSION) {
    throw new Error("Unsupported neighborhood save schemaVersion");
  }
  const quest = asRecord(record.quest, "Neighborhood quest");
  if (
    !QUEST_STATUSES.has(String(quest.status)) ||
    !PARCEL_STATUSES.has(String(quest.parcel)) ||
    !QUEST_OUTCOMES.has(String(quest.outcome))
  ) {
    throw new Error("Neighborhood save has invalid quest state");
  }
  const reputation = asRecord(record.reputation, "Neighborhood reputation");
  for (const tag of REPUTATION_TAGS) {
    if (
      typeof reputation[tag] !== "number" ||
      !Number.isSafeInteger(reputation[tag]) ||
      reputation[tag] < 0
    ) {
      throw new Error(`Neighborhood save has invalid ${tag} reputation`);
    }
  }
  if (!Array.isArray(record.witnessedEvents)) {
    throw new Error("Neighborhood save has invalid witnessed events");
  }
  for (const eventValue of record.witnessedEvents) {
    const event = asRecord(eventValue, "Witnessed event");
    if (
      typeof event.id !== "string" ||
      !REPUTATION_TAGS.includes(event.tag as ReputationTag) ||
      event.source !== "quest" ||
      !Array.isArray(event.witnessIds) ||
      !event.witnessIds.every((id) => typeof id === "string")
    ) {
      throw new Error("Neighborhood save has malformed witnessed event");
    }
  }
  const memories = asRecord(
    record.memoriesByResidentId,
    "Neighborhood memories",
  );
  if (
    !Object.values(memories).every(
      (value) =>
        Array.isArray(value) &&
        value.every((memory) => typeof memory === "string"),
    )
  ) {
    throw new Error("Neighborhood save has invalid resident memories");
  }
  const access = asRecord(record.access, "Neighborhood access");
  if (
    typeof access.blueGate !== "boolean" ||
    typeof access.archiveConversation !== "boolean" ||
    typeof access.rooftopShortcut !== "boolean"
  ) {
    throw new Error("Neighborhood save has invalid access state");
  }
  return structuredClone(parsed) as NeighborhoodState;
}

function asRecord(value: unknown, label: string): Record<string, unknown> {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    throw new Error(`${label} must be an object`);
  }
  return value as Record<string, unknown>;
}
